import * as Rx from 'rxjs';
import * as RxOp from 'rxjs/operators';
import axios, { AxiosError, Method } from 'axios';

import { ApiError } from 'errors/api';
import { apiRequestFormatter } from 'formatters/apiRequest';
import { API_ENDPOINT } from '../settings';
import { apiResponseFormatter } from './../formatters/apiResponse';
import authService from './auth';
import tokenService, { TokenService } from './token';

export class ApiService {
  constructor(
    private apiEndpoint: string,
    private tokenService: TokenService
  ) {}

  public get<T = any>(
    url: string,
    params?: any,
    format = true,
    headers: any = {},
    newUrl?: string
  ): Rx.Observable<T> {
    return this.request<T>('GET', url, params, true, format, headers, newUrl).pipe(
      RxOp.map(({ response }) => response),
      RxOp.filter((response) => response !== undefined)
    );
  }

  public async getPromised<T = any>(url: string, token?: string): Promise<T> {
    const { data } = await axios.get<T>(`${this.apiEndpoint}/${url}`, {
      baseURL: this.apiEndpoint,
      url,
      method: 'GET',
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    return data;
  }

  public post<T = any>(
    url: string,
    body: any,
    format = true
  ): Rx.Observable<T> {
    return this.request<T>('POST', url, body, true, format).pipe(
      RxOp.map(({ response }) => response),
      RxOp.filter((response) => response !== undefined)
    );
  }

  public put<T = any>(url: string, body: any): Rx.Observable<T> {
    return this.request<T>('PUT', url, body).pipe(
      RxOp.map(({ response }) => response),
      RxOp.filter((response) => response !== undefined)
    );
  }

  public delete<T = any>(url: string, params?: any): Rx.Observable<T> {
    return this.request<T>('DELETE', url, params).pipe(
      RxOp.map(({ response }) => response),
      RxOp.filter((response) => response !== undefined)
    );
  }

  public upload<T = any>(url: string, data: FormData) {
    return this.request<T>('POST', url, data);
  }

  private request<T = any>(
    method: Method,
    url: string,
    data: any = null,
    retry: boolean = true,
    format: boolean = true,
    headers: any = {},
    overrideBaseUrl = ''
  ): Rx.Observable<{ response: T; progress: number }> {
    const progress$ = new Rx.BehaviorSubject(0);

    return this.tokenService.getToken().pipe(
      RxOp.first(),
      RxOp.map((token) => {
        if (!token) return headers;
        return { Authorization: `Bearer ${token}`, ...headers };
      }),
      RxOp.switchMap((headers) => {
        return axios.request({
          baseURL: overrideBaseUrl || this.apiEndpoint,
          url,
          method,
          headers: {
            ...headers,
            'Content-Type':
              data instanceof FormData
                ? 'multipart/form-data'
                : 'application/json',
          },
          params: method === 'GET' ? apiRequestFormatter(data) : null,
          data:
            method === 'POST' || method === 'PUT'
              ? apiRequestFormatter(data)
              : null,
          onUploadProgress: (progress: ProgressEvent) => {
            const result = progress.loaded / progress.total;
            progress$.next(result * 100);
          },
        });
      }),
      RxOp.tap(() => progress$.next(100)),
      RxOp.map((res) =>
        format ? apiResponseFormatter<T>(res.data) || null : res.data
      ),
      RxOp.startWith(undefined),
      RxOp.combineLatest(
        progress$.pipe(RxOp.distinctUntilChanged()),
        (response, progress) => ({ response, progress })
      ),
      RxOp.catchError((err) => {
        progress$.complete();
        return this.handleError(err, retry);
      })
    );
  }

  private handleError(err: AxiosError, retry: boolean) {
    if (!err.config || !err.response) return Rx.throwError(err);

    if (err.response.status !== 401 || !retry) {
      return Rx.throwError(new ApiError(err.config, err.response, err));
    }

    authService.logout(true).subscribe();

    return Rx.throwError(new ApiError(err.config, err.response, err));
  }
}

const apiService = new ApiService(API_ENDPOINT, tokenService);
export default apiService;
